/**
 * FIELD VISIT THEMES
 * 
 * Two themes:
 * - SPEC_OPS: Internal-facing tactical aesthetic (Steps 1-5, 7-9)
 * - ELEGANT: Client-facing premium catalog experience (Step 6 only)
 */

export const SPEC_OPS_THEME = {
  name: 'spec-ops',
  
  // Colors
  colors: {
    bgPrimary: '#020617',        // Near black
    bgSurface: '#0f172a',        // Dark slate
    bgElevated: '#1e293b',       // Slate 800
    
    accentGreen: '#10b981',      // Tactical green - primary actions
    accentRed: '#ef4444',        // Red - danger/delete
    accentAmber: '#f59e0b',      // Amber - warnings
    accentGray: '#64748b',       // Slate - secondary
    
    textPrimary: '#e2e8f0',      // Light gray
    textSecondary: '#94a3b8',    // Muted
    textMuted: '#64748b',        // Very muted
    
    border: '#334155',           // Slate 700
    borderLight: '#475569',      // Slate 600
  },
  
  // Tailwind class mappings
  classes: {
    // Backgrounds
    bgPage: 'bg-[#020617]',
    bgCard: 'bg-[#0f172a]',
    bgElevated: 'bg-[#1e293b]',
    bgInput: 'bg-slate-950',
    
    // Text
    textPrimary: 'text-slate-100',
    textSecondary: 'text-slate-400',
    textMuted: 'text-slate-500',
    textAccent: 'text-emerald-400',
    
    // Borders
    border: 'border-slate-700',
    borderLight: 'border-slate-600',
    
    // Buttons
    btnPrimary: 'bg-emerald-600 hover:bg-emerald-500 text-white font-semibold',
    btnSecondary: 'bg-slate-700 hover:bg-slate-600 text-slate-200',
    btnDanger: 'bg-red-600 hover:bg-red-500 text-white',
    btnOutline: 'border-slate-700 text-slate-300 hover:bg-slate-800',
    
    // Inputs
    input: 'bg-slate-950 border-slate-700 text-slate-100 placeholder-slate-500',
    select: 'bg-slate-950 border-slate-700 text-slate-100',
    
    // Cards
    card: 'bg-[#0f172a] border border-slate-700',
    cardElevated: 'bg-[#1e293b] border border-slate-600',
  },
  
  // Typography
  fonts: {
    display: "'Rajdhani', sans-serif",
    mono: "'JetBrains Mono', monospace",
    body: "'Inter', sans-serif",
  },
  
  // Border radius - sharp edges
  radius: {
    none: '0px',
    sm: '2px',
    md: '4px',
  },
};

export const ELEGANT_THEME = {
  name: 'elegant',
  
  // Colors
  colors: {
    bgPrimary: '#ffffff',        // White
    bgSurface: '#f8fafc',        // Slate 50
    bgElevated: '#f1f5f9',       // Slate 100
    
    accentPrimary: '#3b82f6',    // Blue 500
    accentSecondary: '#64748b',  // Slate 500
    accentSuccess: '#10b981',    // Green for selected states
    
    textPrimary: '#1e293b',      // Slate 800
    textSecondary: '#475569',    // Slate 600
    textMuted: '#94a3b8',        // Slate 400
    
    border: '#e2e8f0',           // Slate 200
  },
  
  // Tailwind class mappings
  classes: {
    // Backgrounds
    bgPage: 'bg-white',
    bgCard: 'bg-slate-50',
    bgElevated: 'bg-slate-100',
    bgInput: 'bg-white',
    
    // Text
    textPrimary: 'text-slate-800',
    textSecondary: 'text-slate-600',
    textMuted: 'text-slate-400',
    textAccent: 'text-blue-600',
    
    // Borders
    border: 'border-slate-200',
    borderLight: 'border-slate-100',
    
    // Buttons
    btnPrimary: 'bg-blue-600 hover:bg-blue-500 text-white font-medium shadow-md',
    btnSecondary: 'bg-slate-100 hover:bg-slate-200 text-slate-700',
    btnSuccess: 'bg-emerald-500 hover:bg-emerald-400 text-white',
    btnOutline: 'border-slate-300 text-slate-600 hover:bg-slate-50',
    
    // Inputs
    input: 'bg-white border-slate-300 text-slate-800 placeholder-slate-400 shadow-sm',
    select: 'bg-white border-slate-300 text-slate-800 shadow-sm',
    
    // Cards
    card: 'bg-white border border-slate-200 shadow-md rounded-xl',
    cardElevated: 'bg-slate-50 border border-slate-200 shadow-lg rounded-xl',
    cardProduct: 'bg-white border border-slate-200 shadow-md rounded-xl hover:shadow-lg transition-shadow',
  },
  
  // Typography
  fonts: {
    display: "'Inter', system-ui, sans-serif",
    body: "'Inter', system-ui, sans-serif",
  },
  
  // Border radius - soft feel
  radius: {
    sm: '8px',
    md: '12px',
    lg: '16px',
  },
};

// Step to theme mapping
export const STEP_THEMES = {
  1: 'spec-ops',
  2: 'spec-ops',
  3: 'spec-ops',
  4: 'spec-ops',
  5: 'spec-ops',
  6: 'elegant',    // Product Selection - client facing
  7: 'spec-ops',
  8: 'spec-ops',
  9: 'spec-ops',
};

// Get theme for a step
export const getThemeForStep = (step) => {
  const themeName = STEP_THEMES[step] || 'spec-ops';
  return themeName === 'elegant' ? ELEGANT_THEME : SPEC_OPS_THEME;
};

export default { SPEC_OPS_THEME, ELEGANT_THEME, STEP_THEMES, getThemeForStep };
